import platform from '@play-co/gcinstant';
import { ActionSequence } from 'src/lib/ActionSequence';
import { isPlatformGuest } from 'src/lib/utils';
import { Feature } from 'src/replicant/analytics';
import { Inventory } from 'src/replicant/buruburu/inventory';
import features from 'src/replicant/ruleset/features';
import { Social } from 'src/replicant/social';
import StateObserver from 'src/StateObserver';
import { startInstallNativeSequence } from '../native/native.sequence';
import { socialActions } from '../social/social.redux';
import { socialSequences } from '../social/social.sequence';
import { devSettings } from 'src/lib/settings';
import { openPopupPromise } from 'src/lib/popups/popupOpenClose';
import { isNativePromoAvailable } from '../native/native.util';
import { NativePromoOrigin } from 'src/replicant/native';
import { getDynamicTestBucket } from 'src/replicant/getters/ab';
import ab from 'src/replicant/ruleset/ab';
import { iapClient } from 'src/lib/iap/iapClient';

type SupportedNativePromoOrigin = Extract<
  NativePromoOrigin,
  'gem_shop_icon' | 'gem_plus_button'
>;

export async function showLowGemsBalanceSequence(
  minGems: number,
): Promise<void> {
  const {
    user,
    social: { hasSeenInvitePopup, hasSeenCrossplayPopup },
  } = StateObserver.getState();
  const { isFriendInviteAvailable } = Social.getters(user);

  // User has enough gems, bail.
  if (Inventory.getters(user).getGems() >= minGems) return;

  // Show invite sequence Feature is available and user hasn't seen invite popup on thi session
  if (!hasSeenInvitePopup && isFriendInviteAvailable()) {
    // Mark popup as shown.
    StateObserver.dispatch(socialActions.setHasSeenInvitePopup(true));
    // Start sequence.
    await ActionSequence.start(
      socialSequences.invite(Feature.INVITE.INVITE_UNLOCK),
      'Invite',
    );
  }
}

/**
 * Call this on gems shop purchase attempt, if even IAP disabled
 * If IAP enabled just show gems shop or alternative way to get gems
 * For guest mode we have another dialog
 */
export async function openGemsShopSequence(
  feature: SupportedNativePromoOrigin,
) {
  if (isPlatformGuest(platform)) {
    // On guest we don't have shop and social features for getting gems
    await openPopupPromise('popupNativeLineGuestLogin', {
      origin: 'feature',
    });
  } else if (isIapReady()) {
    await openPopupPromise('popupGemsShop', {});
  } else {
    await showAlternativeWayToGetGems(feature);
  }
}

/**
 * Alternative way how to get gems
 */
async function showAlternativeWayToGetGems(
  feature: SupportedNativePromoOrigin,
) {
  const { user } = StateObserver.getState();

  // Show crossplay promo
  if (features(user).showCrossplayPromoOnGemsShopOpen) {
    await startInstallNativeSequence(feature);
  }

  // Get coins from invite reward if it available
  if (
    features(user).showInvitePopupOnGemsShopOpen &&
    Social.getters(user).isFriendInviteAvailable()
  ) {
    await ActionSequence.start(
      socialSequences.invite(Feature.INVITE.INVITE_INSTEAD_GEMS_SHOP),
      'Invite',
    );
  }

  return false;
}

/**
 * Lock gems shop button state
 */
export function isGemsButtonDisabled() {
  const { user } = StateObserver.getState();
  const IAPEnabled = isIapReady();
  const isGuestMode = isPlatformGuest(platform);

  // Disable gems button if payments are not available
  if (!iapClient.paymentsAvailable) return true;

  // For guest mode always show button
  if (isGuestMode) return false;

  // If any of this feature is active  and IAP is disabled do not lock button
  // but show alternative ways to get gems
  if (!IAPEnabled) {
    // Crossplay promotion, player could buy gems on native platforms
    if (
      features(user).showCrossplayPromoOnGemsShopOpen &&
      isNativePromoAvailable()
    )
      return false;

    // Invite friend to get gems
    if (
      features(user).showInvitePopupOnGemsShopOpen &&
      Social.getters(user).isFriendInviteAvailable()
    )
      return false;
  }

  // In case no one from ab test above running resolve it buy IAP ready value
  return !IAPEnabled;
}

/**
 * Is API enabled helper
 */
function isIapReady(): boolean {
  const { iap } = StateObserver.getState();
  return devSettings.get('enableIAP') || iap.ready;
}
